/**
 * featurebase/FeaturebaseProvider.tsx
 * Manages Featurebase SDK lifecycle: consent state, script loading,
 * JWT retrieval, boot/shutdown, and theme synchronization.
 *
 * Mount inside PrivateAppProvider — requires an authenticated user.
 * SDK loading is asynchronous and never blocks child rendering.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { User } from '@/types/auth';
import { useTheme } from '@/hooks/useTheme';

import { FeaturebaseContext } from './FeaturebaseContext';
import { FEATUREBASE_CONSENT_KEY, IDENTIFY_TIMEOUT_MS, RETRY_COOLDOWN_MS } from './constants';
import { loadFeaturebaseScript, removeFeaturebaseScript } from './loader';
import { featurebaseService } from './service';
import type { FeaturebaseConsentState } from './types/consent';
import type { FeaturebaseReadinessState } from './types/readiness';

// ===== HELPERS =====

/**
 * Read consent state from localStorage with validation.
 * Returns `'unset'` for missing, corrupted, or unrecognized values.
 */
function readConsentFromStorage(): FeaturebaseConsentState {
  try {
    const stored = localStorage.getItem(FEATUREBASE_CONSENT_KEY);
    if (stored === 'granted' || stored === 'denied') {
      return stored;
    }
    return 'unset';
  } catch {
    return 'unset';
  }
}

/**
 * Persist consent state to localStorage.
 * Best-effort — failures are silently swallowed.
 */
function writeConsentToStorage(state: 'granted' | 'denied'): void {
  try {
    localStorage.setItem(FEATUREBASE_CONSENT_KEY, state);
  } catch {
    // localStorage may be full or disabled — non-fatal
  }
}

/**
 * Remove consent key from localStorage.
 * Best-effort — failures are silently swallowed.
 */
function removeConsentFromStorage(): void {
  try {
    localStorage.removeItem(FEATUREBASE_CONSENT_KEY);
  } catch {
    // localStorage may be disabled — non-fatal
  }
}

// ===== PROVIDER =====

export interface FeaturebaseProviderProps {
  user: User;
  children: ReactNode;
}

/**
 * FeaturebaseProvider — Manages consent state and SDK lifecycle.
 *
 * Lifecycle:
 * 1. On mount, reads consent from localStorage
 * 2. When consent is `'granted'`, runs boot sequence: loadScript → getAuthToken → boot → identify
 * 3. Theme changes while `'ready'` call setTheme() (no re-boot)
 * 4. On unmount (logout), shuts down SDK and removes script
 * 5. revokeConsent() performs in-place teardown without unmounting
 *
 * Race safety: A generation counter ensures stale async boot sequences
 * are silently discarded when consent changes or the component unmounts.
 */
export function FeaturebaseProvider({ user, children }: FeaturebaseProviderProps) {
  // ----- State -----
  const [consent, setConsent] = useState<FeaturebaseConsentState>(readConsentFromStorage);
  const [readiness, setReadiness] = useState<FeaturebaseReadinessState>('unloaded');
  const [bootTrigger, setBootTrigger] = useState(0);
  const [feedbackReady, setFeedbackReady] = useState(false);
  const [unreadChangelogCount, setUnreadChangelogCount] = useState<number>(0);

  // ----- Refs -----
  const generationRef = useRef(0);
  const lastRetryRef = useRef(0);

  /** Increment generation counter to invalidate in-flight boot sequences. */
  const invalidateGeneration = useCallback((): number => {
    generationRef.current += 1;
    return generationRef.current;
  }, []);

  // ----- Theme -----
  const { effectiveTheme } = useTheme();
  const themeRef = useRef(effectiveTheme);

  // Keep themeRef in sync so boot effect can read current theme without
  // including effectiveTheme in its dependency array.
  useEffect(() => {
    themeRef.current = effectiveTheme;
  }, [effectiveTheme]);

  // ----- Consent actions -----

  const grantConsent = useCallback(() => {
    writeConsentToStorage('granted');
    setConsent('granted');
  }, []);

  const denyConsent = useCallback(() => {
    writeConsentToStorage('denied');
    setConsent('denied');
  }, []);

  const revokeConsent = useCallback(() => {
    // Invalidate any in-flight boot
    invalidateGeneration();

    // Teardown SDK
    featurebaseService.shutdown();
    removeFeaturebaseScript();

    // Clear persisted consent
    removeConsentFromStorage();

    // Reset state
    setConsent('unset');
    setReadiness('unloaded');
    setFeedbackReady(false);
    setUnreadChangelogCount(0);
  }, [invalidateGeneration]);

  const openChangelogPopup = useCallback((): void => {
    if (readiness !== 'ready') return;
    featurebaseService.openChangelogPopup();
  }, [readiness]);

  const retryInitialization = useCallback((): boolean => {
    if (consent !== 'granted' || readiness !== 'error') {
      return false;
    }

    if (Date.now() - lastRetryRef.current < RETRY_COOLDOWN_MS) {
      return false;
    }

    lastRetryRef.current = Date.now();
    setBootTrigger(prev => prev + 1);
    return true;
  }, [consent, readiness]);

  // ----- Boot lifecycle -----

  useEffect(() => {
    if (consent !== 'granted') {
      return;
    }

    const runId = invalidateGeneration();
    setReadiness('loading');
    setFeedbackReady(false);
    setUnreadChangelogCount(0);

    const boot = async (): Promise<void> => {
      try {
        // Step 1: Load SDK script
        await loadFeaturebaseScript();
        if (runId !== generationRef.current) return;

        // Step 2: Retrieve signed JWT (identity verification — non-negotiable)
        const tokenResult = await featurebaseService.getAuthToken({
          name: user.displayName,
          ...(user.licenseKey ? { plan: user.licenseKey } : {}),
          ...(user.createdAt ? { createdAt: user.createdAt } : {}),
        });
        if (runId !== generationRef.current) return;

        if (!tokenResult.success) {
          setReadiness('error');
          return;
        }

        // Step 3: Boot widget with signed JWT
        const booted = featurebaseService.boot({
          featurebaseJwt: tokenResult.data,
          theme: themeRef.current,
          language: 'en',
        });
        if (runId !== generationRef.current) return;

        setReadiness(booted ? 'ready' : 'error');

        // Step 3.5: Identify user for all widgets (non-fatal, 5s timeout)
        // Authenticates survey, changelog, and portal widgets beyond Messenger.
        // On error or timeout: log telemetry, proceed with degraded auth.
        if (booted) {
          await new Promise<void>((resolve) => {
            let settled = false;
            let timer: ReturnType<typeof setTimeout> | undefined;

            const dispatched = featurebaseService.identify(
              { featurebaseJwt: tokenResult.data },
              (err) => {
                if (settled) return;
                settled = true;
                if (timer !== undefined) clearTimeout(timer);

                if (err && runId === generationRef.current) {
                  console.log(
                    '📊 [FEATUREBASE]',
                    JSON.stringify({
                      event: 'identify_callback_error',
                      timestamp: new Date().toISOString(),
                    })
                  );
                }
                resolve();
              },
            );

            // If dispatch failed (guards), resolve immediately without starting timer
            if (!dispatched) {
              settled = true;
              resolve();
              return;
            }

            timer = setTimeout(() => {
              if (settled) return;
              settled = true;

              if (runId === generationRef.current) {
                console.log(
                  '📊 [FEATUREBASE]',
                  JSON.stringify({
                    event: 'identify_timeout',
                    timeout_ms: IDENTIFY_TIMEOUT_MS,
                    timestamp: new Date().toISOString(),
                  })
                );
              }
              resolve();
            }, IDENTIFY_TIMEOUT_MS);
          });

          if (runId !== generationRef.current) return;
        }

        // Step 4: Initialize feedback widget (non-fatal)
        // Note: The SDK's widgetReady callback is unreliable (may never fire),
        // so we set feedbackReady based on successful init dispatch instead.
        if (booted) {
          const feedbackInitialized = featurebaseService.initFeedbackWidget(
            {
              theme: themeRef.current,
              locale: 'en',
              featurebaseJwt: tokenResult.data,
              metadata: null,
            },
            (err) => {
              if (runId !== generationRef.current) return;

              if (err) {
                console.log(
                  '📊 [FEATUREBASE]',
                  JSON.stringify({
                    event: 'feedback_widget_callback_error',
                    timestamp: new Date().toISOString(),
                  })
                );
              }
            },
          );
          if (feedbackInitialized) {
            setFeedbackReady(true);
          }

          // Step 5: Initialize changelog widget (non-fatal)
          featurebaseService.initChangelogWidget(
            {
              theme: themeRef.current,
              popup: {
                enabled: true,
                usersName: user.displayName,
                autoOpenForNewUpdates: true,
              },
            },
            (err, data) => {
              if (runId !== generationRef.current) return;

              if (err) {
                console.log(
                  '📊 [FEATUREBASE]',
                  JSON.stringify({
                    event: 'changelog_widget_callback_error',
                    timestamp: new Date().toISOString(),
                  })
                );
                return;
              }

              if (data?.action === 'unreadChangelogsCountChanged') {
                setUnreadChangelogCount(data.unreadCount);
              }
            },
          );

          // Step 6: Initialize survey widget (non-fatal)
          featurebaseService.initSurveyWidget(
            {
              theme: themeRef.current,
              placement: 'bottom-right',
              featurebaseJwt: tokenResult.data,
              locale: 'en',
            },
            (err) => {
              if (runId !== generationRef.current) return;

              if (err) {
                console.log(
                  '📊 [FEATUREBASE]',
                  JSON.stringify({
                    event: 'survey_widget_callback_error',
                    timestamp: new Date().toISOString(),
                  })
                );
              }
            },
          );
        }
      } catch {
        // Only set error if this run is still current
        if (runId === generationRef.current) {
          setReadiness('error');
        }
      }
    };

    void boot();

    // Cleanup: teardown on unmount or before re-run
    return () => {
      invalidateGeneration();
      featurebaseService.shutdown();
      removeFeaturebaseScript();
      setReadiness('unloaded');
      setFeedbackReady(false);
      setUnreadChangelogCount(0);
    };
  }, [consent, user.id, user.email, user.displayName, user.licenseKey, user.createdAt, invalidateGeneration, bootTrigger]);

  // ----- Theme synchronization -----

  useEffect(() => {
    if (readiness !== 'ready') return;
    featurebaseService.setTheme(effectiveTheme);
  }, [effectiveTheme, readiness]);

  // ----- Context value -----

  const contextValue = useMemo(
    () => ({ consent, readiness, feedbackReady, unreadChangelogCount, grantConsent, denyConsent, revokeConsent, retryInitialization, openChangelogPopup }),
    [consent, readiness, feedbackReady, unreadChangelogCount, grantConsent, denyConsent, revokeConsent, retryInitialization, openChangelogPopup],
  );

  return (
    <FeaturebaseContext.Provider value={contextValue}>
      {children}
    </FeaturebaseContext.Provider>
  );
}
